import React, { useState, useEffect } from 'react';

interface Sport {
  id: number;
  name: string;
  description?: string;
  icon?: string;
  isActive: boolean;
  createdAt: string;
}

interface SportFormProps {
  sport?: Sport | null;
  onSave: (data: { name: string; description?: string; icon?: string; isActive?: boolean }) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

/**
 * Form component for creating and editing sports
 */
const SportForm: React.FC<SportFormProps> = ({ sport, onSave, onCancel, loading = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: '',
    isActive: true
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (sport) {
      setFormData({
        name: sport.name || '',
        description: sport.description || '',
        icon: sport.icon || '',
        isActive: sport.isActive
      });
    } else {
      setFormData({
        name: '',
        description: '',
        icon: '',
        isActive: true
      });
    }
  }, [sport]);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name ist erforderlich';
    } else if (formData.name.length < 2) {
      newErrors.name = 'Name muss mindestens 2 Zeichen lang sein';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Name darf maximal 100 Zeichen lang sein';
    }

    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'Beschreibung darf maximal 500 Zeichen lang sein';
    }

    if (formData.icon && formData.icon.length > 50) {
      newErrors.icon = 'Icon darf maximal 50 Zeichen lang sein';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      const submitData: any = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        icon: formData.icon.trim() || undefined
      };

      // Only include isActive for updates
      if (sport) {
        submitData.isActive = formData.isActive;
      }

      await onSave(submitData);
    } catch (error) {
      console.error('Error saving sport:', error);
    }
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={headerStyle}>
          <h2 style={titleStyle}>
            {sport ? 'Sportart bearbeiten' : 'Neue Sportart erstellen'}
          </h2>
          <button
            onClick={onCancel}
            style={closeButtonStyle}
            disabled={loading}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} style={formStyle}>
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>
              Name *
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                style={{
                  ...inputStyle,
                  ...(errors.name ? errorInputStyle : {})
                }}
                placeholder="z.B. Fußball"
                disabled={loading}
                maxLength={100}
              />
              {errors.name && <span style={errorTextStyle}>{errors.name}</span>}
            </label>
          </div>

          <div style={fieldGroupStyle}>
            <label style={labelStyle}>
              Beschreibung
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                style={{
                  ...textareaStyle,
                  ...(errors.description ? errorInputStyle : {})
                }}
                placeholder="Beschreibung der Sportart"
                disabled={loading}
                maxLength={500}
                rows={3}
              />
              {errors.description && <span style={errorTextStyle}>{errors.description}</span>}
            </label>
          </div>

          <div style={fieldGroupStyle}>
            <label style={labelStyle}>
              Icon/Emoji
              <input
                type="text"
                value={formData.icon}
                onChange={(e) => handleInputChange('icon', e.target.value)}
                style={{
                  ...inputStyle,
                  ...(errors.icon ? errorInputStyle : {})
                }}
                placeholder="z.B. ⚽"
                disabled={loading}
                maxLength={50}
              />
              {errors.icon && <span style={errorTextStyle}>{errors.icon}</span>}
            </label>
          </div>

          {sport && (
            <div style={fieldGroupStyle}>
              <label style={checkboxLabelStyle}>
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => handleInputChange('isActive', e.target.checked)}
                  style={checkboxStyle}
                  disabled={loading}
                />
                Aktiv
              </label>
            </div>
          )}

          <div style={buttonGroupStyle}>
            <button
              type="button"
              onClick={onCancel}
              style={cancelButtonStyle}
              disabled={loading}
            >
              Abbrechen
            </button>
            <button
              type="submit"
              style={{
                ...saveButtonStyle,
                ...(loading ? disabledButtonStyle : {})
              }}
              disabled={loading}
            >
              {loading ? 'Speichern...' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Styles
const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '20px'
};

const modalStyle: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '8px',
  width: '100%',
  maxWidth: '500px',
  maxHeight: '90vh',
  overflow: 'auto',
  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '20px 24px',
  borderBottom: '1px solid #e5e7eb'
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '1.25rem',
  fontWeight: '600',
  color: '#111827'
};

const closeButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  fontSize: '24px',
  cursor: 'pointer',
  color: '#6b7280',
  padding: '0',
  width: '32px',
  height: '32px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const formStyle: React.CSSProperties = {
  padding: '24px'
};

const fieldGroupStyle: React.CSSProperties = {
  marginBottom: '20px'
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '14px',
  fontWeight: '500',
  color: '#374151',
  marginBottom: '6px'
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  fontSize: '14px',
  marginTop: '4px',
  boxSizing: 'border-box'
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  resize: 'vertical',
  minHeight: '80px'
};

const errorInputStyle: React.CSSProperties = {
  borderColor: '#ef4444'
};

const errorTextStyle: React.CSSProperties = {
  color: '#ef4444',
  fontSize: '12px',
  marginTop: '4px',
  display: 'block'
};

const checkboxLabelStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  fontSize: '14px',
  fontWeight: '500',
  color: '#374151',
  cursor: 'pointer'
};

const checkboxStyle: React.CSSProperties = {
  marginRight: '8px'
};

const buttonGroupStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '12px',
  marginTop: '24px'
};

const cancelButtonStyle: React.CSSProperties = {
  padding: '8px 16px',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  backgroundColor: 'white',
  color: '#374151',
  cursor: 'pointer',
  fontSize: '14px'
};

const saveButtonStyle: React.CSSProperties = {
  padding: '8px 16px',
  border: 'none',
  borderRadius: '6px',
  backgroundColor: '#3b82f6',
  color: 'white',
  cursor: 'pointer',
  fontSize: '14px'
};

const disabledButtonStyle: React.CSSProperties = {
  opacity: 0.6,
  cursor: 'not-allowed'
};

export default SportForm;
